import { d as debounce } from './debounce-CFN_LA0O.js';

/**
 * Custom element for double range input (min/max price filters)
 * Manages synchronized interaction between range sliders and number inputs
 */
class DoubleRangeInput extends HTMLElement {
  #abortController;
  #step = 1;

  connectedCallback() {
    if (!this.#initializeElements()) return;

    // Abort previous controller if reconnecting
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#attachEventListeners();
    this.#initializeInputValues();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #initializeElements() {
    this.minSlider = this.querySelector('input[data-controls="min"]');
    this.maxSlider = this.querySelector('input[data-controls="max"]');

    this.minInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxInput = this.querySelector('input[name="filter.v.price.lte"]');

    return !!(this.minSlider && this.maxSlider && this.minInput && this.maxInput);
  }

  /**
   * Attach event listeners to sliders and number inputs
   * Uses AbortController signal for automatic cleanup
   */
  #attachEventListeners() {
    const { signal } = this.#abortController;

    this.minSlider.addEventListener('input', (event) => this.#handleMinChange(event), { signal });
    this.maxSlider.addEventListener('input', (event) => this.#handleMaxChange(event), { signal });

    this.minInput.addEventListener('input', (event) => this.#handleMinChange(event), {
      signal,
    });
    this.maxInput.addEventListener(
      'input',
      (event) => {
        this.#handleMaxChange(event);
        this.#debouncedValidateMaxInput();
      },
      { signal }
    );
  }

  #initializeInputValues() {
    this.originalMinValue = parseInt(this.dataset.originalMinValue) || 0;
    this.originalMaxValue = parseInt(this.dataset.originalMaxValue) || 10;

    this.minValue = parseInt(this.dataset.currentMinValue || this.originalMinValue);
    this.maxValue = parseInt(this.dataset.currentMaxValue || this.originalMaxValue);

    this.minSlider.value = this.minValue;
    this.maxSlider.value = this.maxValue;

    this.minInput.value = this.minValue;
    this.maxInput.value = this.maxValue;
  }

  /**
   * Handle minimum value changes from slider or number input
   * @param {Event} event - Input event
   */
  #handleMinChange(event) {
    const newValue = parseInt(event.target.value);

    // Ensure min can't go above max minus step
    const maxAllowed = this.maxValue - this.#step;
    if (newValue >= maxAllowed) return this.#handleNewValue('min', maxAllowed);

    this.#handleNewValue('min', newValue);
  }

  /**
   * Handle maximum value changes from slider or number input
   * Different validation logic for sliders vs number inputs
   * @param {Event} event - Input event
   */
  #handleMaxChange(event) {
    const inputValue = event.target.value;
    const newValue = parseInt(inputValue);
    const isNumberInput = event.target === this.maxInput;

    // Ensure max can't go below original min value or above original max value
    if (newValue <= this.originalMinValue || newValue > this.originalMaxValue) {
      return this.#handleNewValue('max', this.originalMaxValue);
    }

    const minAllowed = this.minValue + this.#step;

    // Slider handling only
    if (!isNumberInput) {
      if (newValue < minAllowed) return this.#handleNewValue('max', minAllowed);

      return this.#handleNewValue('max', newValue);
    }

    // Number input handling
    // Don't update the slider if the value is below the current minimum
    // This allows users to type values that will be validated on submit
    if (newValue < minAllowed) return (this.maxInput.value = newValue);

    this.#handleNewValue('max', newValue);
  }

  /**
   * Debounced validation of max input to match form submission timing
   */
  #debouncedValidateMaxInput = debounce(() => {
    this.validateMaxInputBeforeSubmit();
  }, 800);

  validateMaxInputBeforeSubmit() {
    const inputValue = this.maxInput.value;
    const newValue = parseInt(inputValue);

    const minAllowed = this.minValue + this.#step;

    // Correct invalid value to min allowed or update with valid value
    newValue < minAllowed
      ? this.#handleNewValue('max', minAllowed)
      : this.#handleNewValue('max', newValue);
  }

  /**
   * Update all related elements when a value changes
   * Synchronizes slider, number input, ARIA attributes, and CSS custom properties
   * @param {string} controls - Either 'min' or 'max' to indicate which control changed
   * @param {number} newValue - The new value to set
   */
  #handleNewValue(controls, newValue) {
    const { currentValueProperty, input, styleKey } = this.setSliderProperties(controls, newValue);

    this[currentValueProperty] = newValue;
    this[input].value = newValue;
    this[input].ariaValueNow = newValue;

    const valueFraction = newValue / (this.originalMaxValue - this.originalMinValue);
    this.style.setProperty(styleKey, `${valueFraction * 100}%`);

    const numberInput = controls === 'min' ? this.minInput : this.maxInput;
    numberInput.value = newValue;
  }

  /**
   * Get property names and values for slider updates based on control type
   * @param {string} controls - Either 'min' or 'max'
   * @returns {object} Object containing property names and values for updating sliders
   */
  setSliderProperties(controls) {
    const currentValueProperty = controls === 'min' ? 'minValue' : 'maxValue';
    const input = controls === 'min' ? 'minSlider' : 'maxSlider';

    const styleKey = `--${controls}-percent`;

    return {
      currentValueProperty,
      input,
      styleKey,
    };
  }
}

customElements.define('double-range-input', DoubleRangeInput);
